// contexts/WigTransferContext.js
import React, { createContext, useContext, useState } from 'react';
import { API_URL } from '../config/api';
import { getCurrentTimestamp } from '../utils/dateUtils';

const WigTransferContext = createContext();

export const WigTransferProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    orderNo: null,
    currentRoom: null,
    availableRooms: [],
    selectedToRoom: '',
    isLoading: false,
    error: null,
    validationErrors: [],
    transferHistory: []
  });

  // Reset state when modal closes
  const resetTransferState = () => {
    setTransferData(prev => ({
      ...prev,
      selectedToRoom: '',
      error: null,
      validationErrors: [],
      isLoading: false
    }));
  };

  // Open transfer modal for specific order
  const openTransferModal = async (orderNo) => {
  console.log('🔄 Opening transfer modal for order:', orderNo);
  
  setIsModalOpen(true);
  setTransferData(prev => ({ ...prev, orderNo, isLoading: true, error: null }));
  
  try {
    console.log('📡 Starting API calls...');
    
    // Call each API separately with individual error handling
    let currentRoomResult = null;
    let availableRoomsResult = null;
    
    // 1. Fetch current room with detailed error handling
    try {
      console.log('📡 Fetching current room...');
      currentRoomResult = await fetchCurrentRoom(orderNo);
      console.log('✅ Current room loaded:', currentRoomResult);
    } catch (currentRoomError) {
      console.error('❌ Current room API failed:', currentRoomError);
      // Don't fail entirely - some orders might not have a current room yet
      console.log('⚠️ Continuing without current room data...');
    }
    
    // 2. Fetch available rooms with detailed error handling  
    try {
      console.log('📡 Fetching available rooms...');
      availableRoomsResult = await fetchAvailableRooms(orderNo);
      console.log('✅ Available rooms loaded:', availableRoomsResult?.length || 0, 'rooms');
    } catch (availableRoomsError) {
      console.error('❌ Available rooms API failed:', availableRoomsError);
      throw new Error(`Failed to load available rooms: ${availableRoomsError.message}`);
    }
    
    // Success! Both APIs completed
    console.log('✅ All transfer data loaded successfully');
    setTransferData(prev => ({ 
      ...prev, 
      isLoading: false,
      error: null 
    }));
    
  } catch (error) {
    console.error('💥 Fatal error loading transfer data:', error);
    
    // Provide more specific error messages
    let errorMessage = 'Failed to load transfer information';
    
    if (error.message.includes('current-room')) {
      errorMessage = 'Failed to find current room for this order';
    } else if (error.message.includes('available-rooms')) {
      errorMessage = 'Failed to load available rooms for this order';
    } else if (error.message.includes('fetch')) {
      errorMessage = 'Network error - please check your connection';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setTransferData(prev => ({
      ...prev,
      error: errorMessage,
      isLoading: false
    }));
  }
};

  // Close modal and reset state
  const closeTransferModal = () => {
    console.log('🔄 Closing transfer modal');
    setIsModalOpen(false);
    resetTransferState();
  };


 
  // Enhanced fetchCurrentRoom with better error handling
const fetchCurrentRoom = async (orderNo) => {
  console.log(`📡 Calling: ${API_URL}/api/orders/${orderNo}/current-room`);
  
  try {
    const response = await fetch(`${API_URL}/api/orders/${orderNo}/current-room`);
    
    console.log(`📊 Current room API response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Current room API error (${response.status}):`, errorText);
      
      // Try to parse as JSON for better error message
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      } catch {
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
    }
    
    const data = await response.json();
    console.log('📊 Current room data received:', data);
    
    setTransferData(prev => ({
      ...prev,
      currentRoom: data.currentRoom,
      isLoading: false
    }));
    
    return data.currentRoom;
  } catch (error) {
    console.error('💥 fetchCurrentRoom error:', error);
    throw new Error(`current-room API failed: ${error.message}`);
  }
};

// Enhanced fetchAvailableRooms with better error handling
const fetchAvailableRooms = async (orderNo) => {
  console.log(`📡 Calling: ${API_URL}/api/orders/${orderNo}/available-rooms`);
  
  try {
    const response = await fetch(`${API_URL}/api/orders/${orderNo}/available-rooms`);
    
    console.log(`📊 Available rooms API response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Available rooms API error (${response.status}):`, errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      } catch {
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
    }
    
    const data = await response.json();
    console.log('📊 Available rooms data received:', data);
    
    // Validate the response structure
    if (!data.availableRooms) {
      console.warn('⚠️ No availableRooms property in response:', data);
      throw new Error('Invalid response format: missing availableRooms');
    }
    
    if (!Array.isArray(data.availableRooms)) {
      console.warn('⚠️ availableRooms is not an array:', typeof data.availableRooms);
      throw new Error('Invalid response format: availableRooms is not an array');
    }
    
    const rooms = data.availableRooms || [];
    
    setTransferData(prev => ({
      ...prev,
      availableRooms: rooms,
      serverNote: data.note,
      totalRooms: data.totalRooms,
      stats: data.stats // Store the new stats from your updated endpoint
    }));
    
    console.log(`📊 Server returned ${rooms.length} available rooms for order ${orderNo}`);
    if (data.note) {
      console.log(`📝 Server note: ${data.note}`);
    }
    if (data.stats) {
      console.log(`📊 Room stats:`, data.stats);
    }
    
    return rooms;
  } catch (error) {
    console.error('💥 fetchAvailableRooms error:', error);
    throw new Error(`available-rooms API failed: ${error.message}`);
  }
};


  // Validate transfer before execution
 // Replace the validateTransfer function in your WigTransferContext.js
const validateTransfer = async (orderNo, fromRoom, toRoom, taskId = null) => {
  try {
    const response = await fetch(`${API_URL}/api/orders/${orderNo}/validate-transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fromRoom: fromRoom,        // Now expects string format like "5" or "5-123"
        toRoom: toRoom,            // Now expects string format like "7" or "7-456"
        toRoomTaskId: taskId       // Optional task ID for additional validation
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        isValid: false,
        errors: data.errors || [data.message || 'Validation failed']
      };
    }

    return {
      isValid: data.isValid,
      errors: data.errors || [],
      warnings: data.warnings || []
    };
  } catch (error) {
    console.error('Error validating transfer:', error);
    return {
      isValid: false,
      errors: ['Failed to validate transfer. Please try again.']
    };
  }
};


 const getCurrentUser = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData.UserName || userData.username || 'System';
  } catch (error) {
    console.error('Error getting current user:', error);
    return 'System';
  }
};


const executeTransfer = async (assignedWorker = '') => {
  const { orderNo, currentRoom, selectedToRoom } = transferData;
  
  if (!orderNo || !currentRoom || !selectedToRoom) {
    setTransferData(prev => ({
      ...prev,
      error: 'Missing required transfer information'
    }));
    return { success: false };
  }

  setTransferData(prev => ({ ...prev, isLoading: true, error: null }));

  try {
    // Get current user
    const currentUser = getCurrentUser();
    
    console.log(`🔄 Initiating transfer for user: ${currentUser}`);

    // Parse the selected room-task combination
    const [targetRoomNo, targetTaskId] = selectedToRoom.split('-');
    
    // FIXED: Send room-task combinations as strings with dashes (what backend expects)
    const fromRoomStr = currentRoom.taskId ? 
      `${currentRoom.roomNo}-${currentRoom.taskId}` : 
      String(currentRoom.roomNo);
    
    const toRoomStr = targetTaskId ? 
      `${targetRoomNo}-${targetTaskId}` : 
      targetRoomNo;

    // FIXED: Send the payload in the format the backend expects
    const transferPayload = {
      orderNo: parseInt(orderNo),
      fromRoom: fromRoomStr,      // String format: "5" or "5-123"
      toRoom: toRoomStr,          // String format: "7" or "7-456"
      transferredBy: currentUser,

      // Keep the scan data for compatibility
      scanOutData: {
        roomNo: parseInt(currentRoom.roomNo),
        orderNo: parseInt(orderNo)
      },

      scanInData: {
        roomNo: parseInt(targetRoomNo),
        orderNo: parseInt(orderNo),
        taskId: targetTaskId || null,
        ...(assignedWorker && assignedWorker.trim() !== '' ? { assignedWorker } : {})
      }
    };

    // Only include assignedWorker at the top level if explicitly selected
    if (assignedWorker && assignedWorker.trim() !== '') {
      transferPayload.assignedWorker = assignedWorker;
    }


    // Validate the transfer before executing it
    const validation = await validateTransfer(
      parseInt(orderNo),
      fromRoomStr,  // Send as string with task ID if available
      toRoomStr,    // Send as string with task ID if available
      targetTaskId  // Still pass task ID separately for validation
    );

    if (!validation.isValid) {
      setTransferData(prev => ({
        ...prev,
        validationErrors: validation.errors,
        isLoading: false
      }));
      return {
        success: false,
        errors: validation.errors
      };
    }

    if (validation.warnings && validation.warnings.length > 0) {
      console.warn('Transfer warnings:', validation.warnings);
    }

    const response = await fetch(`${API_URL}/api/orders/${orderNo}/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(transferPayload)
    });

    const data = await response.json();
    console.log('🔍 DEBUG: Backend response:', data);

    if (!response.ok) {
      console.error('🔍 DEBUG: Backend error response:', data);
      throw new Error(data.message || 'Transfer failed');
    }

    console.log('✅ Transfer completed successfully:', data);

    // Success! Refresh data
    await Promise.all([
      fetchCurrentRoom(orderNo),
    ]);

    setTransferData(prev => ({
      ...prev,
      selectedToRoom: '',
      validationErrors: [],
      isLoading: false
    }));

    return { 
      success: true, 
      message: data.message || `Wig transferred from Room ${currentRoom.roomNo} to Room ${targetRoomNo}`,
      newCurrentRoom: data.newCurrentRoom || toRoomStr,
      transferDetails: data.transferDetails
    };

  } catch (error) {
    console.error('❌ Error executing transfer:', error);
    setTransferData(prev => ({
      ...prev,
      error: error.message || 'Transfer failed. Please try again.',
      isLoading: false
    }));
    return { success: false, error: error.message };
  }
};

  const completeOrder = async (orderNo) => {    
    setTransferData(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const currentUser = getCurrentUser();
      console.log('👤 Completing order as user:', currentUser);
      
      // Use the correct endpoint that exists in your backend
      const response = await fetch(`${API_URL}/api/orders/${orderNo}/auto-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          executedBy: currentUser,
          executedAt: getCurrentTimestamp()
        })
      });
      
      console.log('📡 Complete order response status:', response.status);
      
      const data = await response.json();
      console.log('📋 Complete order response data:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to complete order');
      }
      
      setTransferData(prev => ({ ...prev, isLoading: false }));
      
      return { success: true, data };
    } catch (error) {
      console.error('❌ Error completing order:', error);
      setTransferData(prev => ({ 
        ...prev, 
        error: error.message,
        isLoading: false 
      }));
      return { success: false, error: error.message };
    }
  };

  // Update selected room
  const setSelectedToRoom = (roomNo) => {
    setTransferData(prev => ({
      ...prev,
      selectedToRoom: roomNo,
      validationErrors: [], // Clear previous validation errors
      error: null
    }));
  };

  const value = {
    // State
    isModalOpen,
    transferData,
    
    // Actions
    openTransferModal,
    closeTransferModal,
    setSelectedToRoom,
    executeTransfer,
    validateTransfer,
    getCurrentUser,
    completeOrder,
    // Computed values
    canTransfer: transferData.currentRoom && transferData.selectedToRoom && 
                 transferData.selectedToRoom !== transferData.currentRoom?.roomNo,
    hasValidationErrors: transferData.validationErrors.length > 0,
    
    // Utils
    resetTransferState
  };

  return (
    <WigTransferContext.Provider value={value}>
      {children}
    </WigTransferContext.Provider>
  );
};

// Custom hook to use the context
export const useWigTransfer = () => {
  const context = useContext(WigTransferContext);
  if (!context) {
    throw new Error('useWigTransfer must be used within a WigTransferProvider');
  }
  return context;
};